import { useAuth } from '../../hooks/useAuth';
import { useConversation } from '../../contexts/ConversationContext';
import DateRangePicker from './DateRangePicker';

interface HeaderProps {
  onToggleSidebar: () => void;
  onToggleChat: () => void;
  isChatOpen: boolean;
  isMobile?: boolean;
}

export default function Header({ onToggleSidebar, onToggleChat, isChatOpen, isMobile }: HeaderProps) {
  const { user, logout } = useAuth();
  const { clearConversation } = useConversation();

  return (
    <header className="h-11 md:h-12 bg-black text-white flex items-center justify-between px-2 md:px-3 flex-shrink-0 relative" style={{ zIndex: 100 }}>
      {/* Left section */}
      <div className="flex items-center gap-2 md:gap-4">
        {/* Menu toggle */}
        <button
          onClick={onToggleSidebar}
          className="p-2 hover:bg-white/10 active:bg-white/20 text-white transition-colors rounded-sm"
          aria-label="Toggle sidebar"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        {/* Logo and title - title hidden on mobile */}
        <div className="hidden md:flex items-center gap-2">
          <div className="w-7 h-7 bg-mint flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-black" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          </div>
          <span className="font-light text-white text-sm">LLM Dashboard</span>
        </div>
      </div>

      {/* Center section - Date picker (hidden on small mobile) */}
      <div className="hidden sm:flex items-center">
        <DateRangePicker />
      </div>

      {/* Right section */}
      <div className="flex items-center gap-1 md:gap-2">
        {/* Mobile date picker */}
        <div className="sm:hidden">
          <DateRangePicker />
        </div>

        {/* Clear conversation */}
        <button
          onClick={clearConversation}
          className="p-2 hover:bg-white/10 active:bg-white/20 text-white transition-colors rounded-sm"
          title="Clear conversation"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>

        {/* Toggle chat - hidden on mobile (uses FAB instead) */}
        {!isMobile && (
          <button
            onClick={onToggleChat}
            className={`p-2 hover:bg-white/10 active:bg-white/20 transition-colors rounded-sm ${
              isChatOpen ? 'text-mint' : 'text-white'
            }`}
            title={isChatOpen ? 'Hide chat' : 'Show chat'}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
          </button>
        )}

        {/* User menu */}
        <div className="flex items-center gap-2 md:gap-3 pl-2 md:pl-3 ml-1 md:ml-2 border-l border-white/20">
          <div className="text-right hidden md:block">
            <div className="text-sm font-light text-white truncate max-w-[120px] lg:max-w-none">
              {user?.email || 'User'}
            </div>
            <div className="font-mono text-xs text-grey-medium uppercase tracking-wider">
              {user?.clientId || 'Unknown'}
            </div>
          </div>
          <button
            onClick={logout}
            className="p-2 hover:bg-white/10 active:bg-white/20 text-white transition-colors rounded-sm"
            title="Sign out"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
          </button>
        </div>
      </div>
    </header>
  );
}
